import type { BiomassDataRow } from '../../utils/productionUnitTable'
import type { DisplayedEndOfDayStock } from '../../utils/productionEndOfDayStock'
import type { DailyMortalityRate } from '../../utils/dailyMortalityRate'
import { toDateInputValue } from '../../utils/dateNormalization'
import { escapeCsvCell } from './datatableHelpers'

// Mirrors the shape produced by `computeMortalityRollupByRowId` in
// src/screens/biomass/mortalityLogBridge.ts. Re-declared here so the helper
// stays decoupled from the bridge module (and from React).
export type MortalityRollupSummary = {
  totalDailyMortality: string
  eventCount: number
  primaryLogId: string | undefined
}

export type ProductionCsvExportInput = {
  rows: ReadonlyArray<BiomassDataRow>
  columns: ReadonlyArray<string>
  mortalityRollupByRowId: ReadonlyMap<string, MortalityRollupSummary>
  displayedEndOfDayStockByRowId: ReadonlyMap<string, DisplayedEndOfDayStock>
  // Track A finding 3.1: the synthetic daily-mortality-% column. Optional so
  // existing callers/tests keep working; when absent the column exports blank.
  dailyMortalityRateByRowId?: ReadonlyMap<string, DailyMortalityRate>
}

const ADMIN_TAIL: ReadonlyArray<string> = ['id', 'lotId', 'unmappedLot']
const ADMIN_SET = new Set(ADMIN_TAIL)

// Header order:
//   1. Data columns (display order, with id/lotId/unmappedLot removed).
//   2. Sibling source columns for derived cells (events vs stored, derived vs stored).
//   3. Administrative id/lotId/unmappedLot at the tail (finding #14).
export const buildProductionExportHeaders = (
  columns: ReadonlyArray<string>
): string[] => {
  const dataColumns = columns.filter((c) => !ADMIN_SET.has(c))
  const sourceColumns: string[] = []
  if (dataColumns.includes('dailyMortality')) sourceColumns.push('dailyMortality_source')
  if (dataColumns.includes('fishStock')) sourceColumns.push('fishStock_source')
  return [...dataColumns, ...sourceColumns, ...ADMIN_TAIL]
}

const readStringField = (row: BiomassDataRow, key: string): string => {
  const raw = (row as Record<string, unknown>)[key]
  if (raw === undefined || raw === null) return ''
  if (typeof raw === 'string') return raw
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw)
  return ''
}

// Project one cell using the same derivation the Database screen renders:
//   - dailyMortality: MORT-12 rollup overrides stored when events exist (matches
//     Datatable.tsx ~L2069 — the on-screen cell becomes read-only and shows the
//     events sum).
//   - fishStock: `computeDisplayedEndOfDayStockByRowId` already prefers stored
//     over derived internally, so we just read `derived.value` when present.
//   - dailyMortality_source / fishStock_source: audit columns so the operator
//     can tell which cells came from events / derivation vs the stored row.
export const projectProductionExportCell = (args: {
  row: BiomassDataRow
  header: string
  mortalityRollupByRowId: ReadonlyMap<string, MortalityRollupSummary>
  displayedEndOfDayStockByRowId: ReadonlyMap<string, DisplayedEndOfDayStock>
  dailyMortalityRateByRowId?: ReadonlyMap<string, DailyMortalityRate>
}): string => {
  const { row, header, mortalityRollupByRowId, displayedEndOfDayStockByRowId, dailyMortalityRateByRowId } = args
  const rowId = String(row.id ?? '')

  if (header === 'dailyMortality') {
    const rollup = mortalityRollupByRowId.get(rowId)
    if (rollup && rollup.eventCount > 0) return rollup.totalDailyMortality
    return readStringField(row, 'dailyMortality')
  }

  if (header === 'dailyMortalityRate') {
    const rate = dailyMortalityRateByRowId?.get(rowId)
    return rate && rate.ratePct !== null ? rate.ratePct.toFixed(2) : ''
  }

  if (header === 'dailyMortality_source') {
    const rollup = mortalityRollupByRowId.get(rowId)
    if (rollup && rollup.eventCount > 0) return 'events'
    return readStringField(row, 'dailyMortality').trim() ? 'stored' : ''
  }

  if (header === 'fishStock') {
    const derived = displayedEndOfDayStockByRowId.get(rowId)
    if (derived && derived.value) return derived.value
    return readStringField(row, 'fishStock')
  }

  if (header === 'fishStock_source') {
    const derived = displayedEndOfDayStockByRowId.get(rowId)
    if (derived && derived.source !== 'unknown' && derived.value) return derived.source
    return readStringField(row, 'fishStock').trim() ? 'stored' : ''
  }

  if (header === 'date') {
    const raw = readStringField(row, 'date')
    return raw ? toDateInputValue(raw) : ''
  }

  return readStringField(row, header)
}

export const buildProductionExportCsv = (input: ProductionCsvExportInput): string => {
  const headers = buildProductionExportHeaders(input.columns)
  const lines: string[] = [headers.join(',')]
  for (const row of input.rows) {
    const cells = headers.map((header) =>
      escapeCsvCell(
        projectProductionExportCell({
          row,
          header,
          mortalityRollupByRowId: input.mortalityRollupByRowId,
          displayedEndOfDayStockByRowId: input.displayedEndOfDayStockByRowId,
          dailyMortalityRateByRowId: input.dailyMortalityRateByRowId
        })
      )
    )
    lines.push(cells.join(','))
  }
  return lines.join('\n')
}
